import { authHeader, accountHeader, BASE_URL, handleResponse } from '@helpers/api';
import { ProductEvent } from '@constants/product-events';
import { captureOnCreate } from '@helpers/product-events';

export const authService = {
    login,
    loginWithGoogle,
    getCurrentUser,
    getCurrentSession,
    endCurrentSession,
    updateUser,
    updatePersonalSettings,
    updateNavigationBookmarks,
    uploadProfileImage,
    deleteProfileImage,
    updateLastVisit,
    forgotPassword,
    resetPassword,
    getEmailByToken,
    deleteUser,
    signup,
    verifyEmail,
    resendEmail,
    validateInvitation,
    acceptInvitation,
    acceptInvitationWithGoogle,
};

function getEmailByToken(resetToken) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetToken }),
    };

    return fetch(`${BASE_URL}api/identity/emailbytoken`, requestOptions)
        .then(response => response.json())
        .then(data => data);
}

function forgotPassword(email) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    };

    return fetch(`${BASE_URL}api/identity/forgot-password`, requestOptions).then(handleResponse);
}

function resetPassword(password, resetToken) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, resetToken }),
    };

    return fetch(`${BASE_URL}api/identity/reset-password`, requestOptions).then(handleResponse);
}

function updateUser(user) {
    const requestOptions = {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(user),
    };
    return fetch(`${BASE_URL}api/users`, requestOptions)
        .then(handleResponse)
        .then(captureOnCreate(user, ProductEvent.UserInvited, () => ({ teams: user.teamIds?.length })));
}

function updatePersonalSettings(values) {
    const requestOptions = {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(values),
    };
    return fetch(`${BASE_URL}api/users/personal-settings`, requestOptions)
        .then(handleResponse)
        .then(data => data);
}

function updateNavigationBookmarks(bookmarks) {
    return fetch(`${BASE_URL}api/users/navigation-bookmarks`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(bookmarks),
    }).then(handleResponse);
}

function uploadProfileImage(formData) {
    const user = JSON.parse(localStorage.getItem('user'));
    const requestOptions = {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + user.token,
            ...accountHeader(),
        },
        body: formData,
    };
    return fetch(`${BASE_URL}api/users/upload-profile-image`, requestOptions).then(handleResponse);
}

function deleteProfileImage() {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };
    return fetch(`${BASE_URL}api/users/profile-image`, requestOptions).then(handleResponse);
}

function updateLastVisit(lastVisit) {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ LastVisit: lastVisit }),
    };

    return fetch(`${BASE_URL}api/users/last-visit`, requestOptions).then(handleResponse);
}

function login(username, password) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...accountHeader() },
        body: JSON.stringify({ Email: username, Password: password }),
    };

    return fetch(`${BASE_URL}api/identity/authenticate`, requestOptions).then(handleResponse);
}

function loginWithGoogle(data) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...accountHeader() },
        body: JSON.stringify(data),
    };

    return fetch(`${BASE_URL}api/identity/login-with-google`, requestOptions).then(handleResponse);
}

function getCurrentUser() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/users/current-user`, requestOptions)
        .then(handleResponse)
        .then(user => user);
}

function getCurrentSession() {
    return fetch(`${BASE_URL}api/users/current-session`, { method: 'GET', headers: authHeader() }).then(response =>
        response.ok ? response.json() : Promise.reject(new Error(`current-session ${response.status}`)),
    );
}

function endCurrentSession() {
    return fetch(`${BASE_URL}api/users/current-session`, {
        method: 'DELETE',
        headers: authHeader(),
        keepalive: true,
        signal: AbortSignal.timeout(5000),
    }).then(response =>
        response.ok || response.status === 401
            ? undefined
            : Promise.reject(new Error(`current-session ${response.status}`)),
    );
}

function deleteUser(id) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/users/${id}`, requestOptions).then(handleResponse);
}

function signup(values) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
    };

    return fetch(`${BASE_URL}api/identity/registration`, requestOptions)
        .then(handleResponse)
        .then(user => user);
}

function verifyEmail(token) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    };

    return fetch(`${BASE_URL}api/identity/validatebytoken/${token}`, requestOptions)
        .then(handleResponse)
        .then(user => user);
}

function resendEmail() {
    const requestOptions = {
        method: 'POST',
        headers: authHeader(),
    };

    return fetch(`${BASE_URL}api/identity/resend-email-verification`, requestOptions).then(handleResponse);
}

function validateInvitation(token) {
    const requestOptions = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    };

    return fetch(`${BASE_URL}api/identity/invitation/${token}`, requestOptions).then(handleResponse);
}

function acceptInvitation(token, password) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
    };

    return fetch(`${BASE_URL}api/identity/accept-invitation`, requestOptions).then(handleResponse);
}

function acceptInvitationWithGoogle(token, code, redirectUri) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code, redirectUri }),
    };

    return fetch(`${BASE_URL}api/identity/accept-invitation-with-google`, requestOptions).then(handleResponse);
}
